import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { TextFieldStyles } from "../../../styles/commonStyles";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchFieldsStart } from "../../../store/features/customer/fields/fieldsSlice";
import { NOTE_TYPES } from "./constants";
import FormActionButton from "../../buttons/formActionButton.component";
import { AddPhotoAlternate, Delete } from "@mui/icons-material";
import ImageContainer from "./imageContainer.component";
import Map from "../../map/map.component";
import {
  createFieldNoteStart,
  deleteFieldNoteStart,
  updateFieldNoteStart,
} from "../../../store/features/fieldNote/fieldNoteSlice";
import { Controller } from "react-hook-form";
import CustomAlert from "../../alert/alert.component";
import { useTheme } from "@emotion/react";
import { fetchDiseasesStart } from "../../../store/features/diseases/diseasesSlice";
import { useNoteForm } from "../../../hook/fieldNotebook/useNoteForm";
import CustomDialog from "../../muiDialogs/dialog.component";
import { dialogMessages } from "../utils";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isValidFieldNotePosition } from "../../../utils/fieldNotebookUtils";

const NoteForm = ({
  isMobile,
  mode = "create",
  initialData = null,
  onSuccess = null,
  isFieldPage,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    form: {
      control,
      handleSubmit,
      watch,
      formState: { errors, isSubmitted },
    },
    fileInputRef,
    images,
    diseaseLocation,
    isLoading,
    error,
    isEditMode,
    handleFileChange,
    handleRemoveImage,
    handleAddPhotoClick,
    handleMarkerLocationSelect,
  } = useNoteForm(mode, initialData, onSuccess, isFieldPage);

  const fields = useSelector((state) => state.fields?.data?.data);
  const accessToken = useSelector((state) => state.auth?.user?.access_token);
  const diseaseList = useSelector((state) => state.diseases?.data?.data);
  const selectedField = watch("field");
  const selectedType = watch("noteType");

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const onSubmit = (data) => {
    if (
      data.noteType === "DISEASE_REPORT" &&
      !isValidFieldNotePosition(diseaseLocation)
    ) {
      return;
    }
    const formData = {
      field: data.field,
      note_type: data.noteType,
      title: data.title,
      description: data.noteText,
    };

    if (data.noteType === "DISEASE_REPORT") {
      formData.disease = data.diseaseType;
      formData.position = diseaseLocation;
    }

    if (isEditMode) {
      const existingPhotos = images
        .filter((img) => img.isExisting && img.id)
        .map((img) => img.id);

      const newPhotos = images
        .filter((img) => !img.isExisting && img.file)
        .map((img) => img.file);

      formData.photos = JSON.stringify(existingPhotos);
      formData.newPhotos = newPhotos;

      dispatch(
        updateFieldNoteStart({
          noteId: initialData.id,
          data: formData,
        }),
      );
    } else {
      formData.photo = images.map((img) => img.file).filter(Boolean);

      if (data.noteType === "DISEASE_REPORT") {
        formData.disease = data.diseaseType;
        formData.position = diseaseLocation;
      }

      dispatch(
        createFieldNoteStart({ data: formData, accessToken: accessToken }),
      );
    }
  };

  useEffect(() => {
    dispatch(fetchFieldsStart({ accessToken }));
    dispatch(fetchDiseasesStart());
  }, [dispatch, accessToken]);

  const selectedFieldData = fields?.find((field) => field.id === selectedField);
  const fieldPolygon = selectedFieldData?.polygon || null;
  const fieldCenter = selectedFieldData?.polygon?.[0] || [40.0647, 9.1217];

  const handleDeleteNote = () => {
    dispatch(deleteFieldNoteStart({ noteId: initialData.id, accessToken }));
  };
  const handleDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      height="100%"
      width="100%"
      display="flex"
      flexDirection="column"
    >
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto",
          p: 2,
        }}
        elevation={0}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="center"
          width="100%"
        >
          <Typography
            fontSize="12px"
            color={theme.palette.custom.infoTextColor}
            sx={{ width: isMobile ? "100%" : 430 }}
          >
            Consequat eiusmod dolore nulla consectetur. Ad esse enim sunt tempor
            cupidatat ad consectetur sit eu. Magna qui aliqua culpa anim officia
            deserunt in eu laboris reprehenderit consectetur laborum. Ex
            cupidatat eu sunt velit ut ipsum consectetur. Sit cupidatat
            voluptate proident cillum.
          </Typography>
          <Controller
            name="field"
            control={control}
            rules={{ required: "Il campo terreno è obbligatorio" }}
            render={({ field }) => (
              <TextField
                {...field}
                variant="filled"
                id="filled-select-field"
                label="Terreno"
                select
                sx={{ ...TextFieldStyles, width: isMobile ? "100%" : 430 }}
                fullWidth
                disabled={isLoading || isFieldPage}
                error={!!errors.field}
                helperText={errors.field?.message}
              >
                {fields && fields.length > 0 ? (
                  fields.map((field) => (
                    <MenuItem key={field.id} value={field.id}>
                      {field.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">Nessun terreno disponibile</MenuItem>
                )}
              </TextField>
            )}
          />
          <Controller
            name="noteType"
            control={control}
            rules={{ required: "La tipologia nota è obbligatoria" }}
            render={({ field }) => (
              <TextField
                {...field}
                variant="filled"
                id="filled-select-type"
                label="Tipologia nota"
                select
                sx={{ ...TextFieldStyles, width: isMobile ? "100%" : 430 }}
                fullWidth
                disabled={isLoading}
                error={!!errors.noteType}
                helperText={errors.noteType?.message}
              >
                {NOTE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          {selectedType && (
            <Controller
              name="title"
              control={control}
              rules={{ required: "Il titolo è obbligatorio" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="filled"
                  id="filled-title"
                  label="Titolo"
                  sx={{
                    width: isMobile ? "100%" : 430,
                    height: "auto",
                    "& .MuiFilledInput-root": {
                      backgroundColor: "#E9EEF0",
                    },
                  }}
                  disabled={isLoading}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />
          )}

          {selectedType === "DISEASE_REPORT" && (
            <>
              <Controller
                name="diseaseType"
                control={control}
                rules={{ required: "La tipologia malattia è obbligatoria" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="filled"
                    id="filled-select-disease"
                    label="Tipologia malattia"
                    select
                    sx={{ ...TextFieldStyles, width: isMobile ? "100%" : 430 }}
                    fullWidth
                    disabled={isLoading}
                    error={!!errors.diseaseType}
                    helperText={errors.diseaseType?.message}
                  >
                    {diseaseList?.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                width={isMobile ? "100%" : "auto"}
              >
                <Typography fontSize="14px">
                  Scegli il punto sulla mappa
                </Typography>
                <Box width={isMobile ? "100%" : 430} height={260} zIndex={0}>
                  <Map
                    zoomLevel={7}
                    fieldPosition={fieldCenter}
                    polygonCoordinates={fieldPolygon}
                    isMarkerMode
                    onMarkerCreated={handleMarkerLocationSelect}
                    markerPosition={diseaseLocation}
                  />
                </Box>
                {isSubmitted &&
                  !isValidFieldNotePosition(diseaseLocation) && (
                    <Typography fontSize="12px" color="error">
                      La posizione GPS è obbligatoria per le malattie
                    </Typography>
                  )}
              </Box>
            </>
          )}
          {selectedType && (
            <>
              <Controller
                name="noteText"
                control={control}
                rules={{
                  required:
                    selectedType !== "disease"
                      ? "Il testo della nota è obbligatorio"
                      : false,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="filled"
                    id="filled-note-text"
                    label="Nota"
                    multiline
                    minRows={5}
                    sx={{
                      width: isMobile ? "100%" : 430,
                      height: "auto",
                      "& .MuiFilledInput-root": {
                        backgroundColor: "#E9EEF0",
                      },
                    }}
                    disabled={isLoading}
                    error={!!errors.noteText}
                    helperText={errors.noteText?.message}
                  />
                )}
              />

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={isLoading}
              />
              <FormActionButton
                startIcon={<AddPhotoAlternate />}
                label={"Aggiungi foto"}
                style={{
                  width: isMobile ? "100%" : 430,
                  textTransform: "none",
                  backgroundColor: "#000000",
                  color: "#ffffff",
                  "&:hover": { backgroundColor: "#333333" },
                }}
                onClick={handleAddPhotoClick}
                disabled={isLoading}
              />
              <ImageContainer
                images={images}
                onRemove={handleRemoveImage}
                isMobile={isMobile}
              />
            </>
          )}
        </Box>
      </Paper>
      <Box display="flex" width="100%" justifyContent="end" mt={2} gap={1}>
        {isEditMode && (
          <FormActionButton
            startIcon={<Delete />}
            label="Elimina nota"
            color="error"
            onClick={handleDeleteDialog}
          />
        )}
        <FormActionButton
          label={
            isLoading
              ? "Salvataggio..."
              : isEditMode
                ? "Aggiorna nota"
                : "Salva nota"
          }
          disabled={isLoading}
          type="submit"
        />
      </Box>
      {error && (
        <CustomAlert
          severity="error"
          message={getErrorMessage(error, {
            fallback:
              "Errore durante il salvataggio della nota. Riprova più tardi",
          })}
        />
      )}
      <CustomDialog
        open={openDeleteDialog}
        title={dialogMessages.noteBook.delete.title}
        description={dialogMessages.noteBook.delete.description}
        onConfirm={handleDeleteNote}
        onClose={() => setOpenDeleteDialog(false)}
      />
    </Box>
  );
};

export default NoteForm;
